import { useCallback, useMemo } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { StoredRecording } from '@/lib/db'

type GradingChartDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  recordings: StoredRecording[]
}

export const GradingChartDialog = ({ open, onOpenChange, recordings }: GradingChartDialogProps) => {
  // 単語の発音を再生
  const playWordPronunciation = useCallback((word: string) => {
    // Web Speech API を使用
    if ('speechSynthesis' in window) {
      // 既存の発話を停止
      window.speechSynthesis.cancel()

      const utterance = new SpeechSynthesisUtterance(word)
      utterance.lang = 'en-US' // 英語の発音
      utterance.rate = 0.8 // 少しゆっくり
      window.speechSynthesis.speak(utterance)
    }
  }, [])
  // 採点済みの録音のみをフィルタリング
  const gradedRecordings = useMemo(() => {
    return recordings.filter((r) => r.gradingResult).sort((a, b) => a.savedAt - b.savedAt)
  }, [recordings])

  // スコア推移データ
  const scoreTimelineData = useMemo(() => {
    return gradedRecordings
      .filter((recording) => recording.gradingResult?.raw_response?.NBest?.[0])
      .map((recording, index) => {
        const assessment = recording.gradingResult!.raw_response.NBest[0].PronunciationAssessment
        return {
          index: index + 1,
          date: new Date(recording.savedAt).toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' }),
          pronunciation: Math.round(assessment.PronScore * 100) / 100,
          accuracy: Math.round(assessment.AccuracyScore * 100) / 100,
          fluency: Math.round(assessment.FluencyScore * 100) / 100,
          prosody: Math.round(assessment.ProsodyScore * 100) / 100,
          completeness: Math.round(assessment.CompletenessScore * 100) / 100,
        }
      })
  }, [gradedRecordings])

  // 平均スコアデータ
  const averageScoreData = useMemo(() => {
    const validRecordings = gradedRecordings.filter((r) => r.gradingResult?.raw_response?.NBest?.[0])

    if (validRecordings.length === 0) return []

    const sum = validRecordings.reduce(
      (acc, r) => {
        const assessment = r.gradingResult!.raw_response.NBest[0].PronunciationAssessment
        return {
          pronunciation: acc.pronunciation + assessment.PronScore,
          accuracy: acc.accuracy + assessment.AccuracyScore,
          fluency: acc.fluency + assessment.FluencyScore,
          prosody: acc.prosody + assessment.ProsodyScore,
          completeness: acc.completeness + assessment.CompletenessScore,
        }
      },
      { pronunciation: 0, accuracy: 0, fluency: 0, prosody: 0, completeness: 0 },
    )

    const count = validRecordings.length

    return [
      { name: '発音', score: Math.round((sum.pronunciation / count) * 100) / 100 },
      { name: '正確性', score: Math.round((sum.accuracy / count) * 100) / 100 },
      { name: '流暢性', score: Math.round((sum.fluency / count) * 100) / 100 },
      { name: '韻律', score: Math.round((sum.prosody / count) * 100) / 100 },
      { name: '完全性', score: Math.round((sum.completeness / count) * 100) / 100 },
    ]
  }, [gradedRecordings])

  // 音素統計データ
  const phonemeStatsData = useMemo(() => {
    if (gradedRecordings.length === 0) return []

    // 全ての音素を集計
    const phonemeMap = new Map<string, { count: number; totalScore: number }>()

    gradedRecordings.forEach((recording) => {
      const phonemeStats = recording.gradingResult?.analysis.phoneme_statistics
      if (phonemeStats) {
        Object.entries(phonemeStats).forEach(([phoneme, stat]) => {
          const existing = phonemeMap.get(phoneme)
          if (existing) {
            existing.count += stat.count
            existing.totalScore += stat.average_score * stat.count
          } else {
            phonemeMap.set(phoneme, {
              count: stat.count,
              totalScore: stat.average_score * stat.count,
            })
          }
        })
      }
    })

    // 出現回数が多い順にソートして上位10個
    return Array.from(phonemeMap.entries())
      .map(([phoneme, data]) => ({
        phoneme,
        count: data.count,
        averageScore: Math.round((data.totalScore / data.count) * 100) / 100,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [gradedRecordings])

  // 苦手な単語リストデータ
  const weakWordsData = useMemo(() => {
    if (gradedRecordings.length === 0) return []

    // 全ての苦手な単語を集計
    const wordMap = new Map<string, { count: number; totalScore: number }>()

    gradedRecordings.forEach((recording) => {
      const weakWords = recording.gradingResult?.analysis.weak_words
      if (weakWords) {
        weakWords.forEach((word) => {
          const existing = wordMap.get(word.word)
          if (existing) {
            existing.count += 1
            existing.totalScore += word.score
          } else {
            wordMap.set(word.word, {
              count: 1,
              totalScore: word.score,
            })
          }
        })
      }
    })

    // 出現回数が多い順にソートして上位15個
    return Array.from(wordMap.entries())
      .map(([word, data]) => ({
        word,
        count: data.count,
        averageScore: Math.round((data.totalScore / data.count) * 100) / 100,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15)
  }, [gradedRecordings])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>採点結果の分析</DialogTitle>
          <DialogDescription>{gradedRecordings.length}件の採点データを分析</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="timeline">スコア推移</TabsTrigger>
            <TabsTrigger value="average">平均スコア</TabsTrigger>
            <TabsTrigger value="phoneme">音素分析</TabsTrigger>
            <TabsTrigger value="weakWords">苦手な単語</TabsTrigger>
          </TabsList>

          {/* スコア推移 */}
          <TabsContent value="timeline" className="space-y-4">
            <div className="h-100 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={scoreTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="pronunciation" stroke="#22c55e" name="発音" strokeWidth={2} />
                  <Line type="monotone" dataKey="accuracy" stroke="#3b82f6" name="正確性" strokeWidth={2} />
                  <Line type="monotone" dataKey="fluency" stroke="#a855f7" name="流暢性" strokeWidth={2} />
                  <Line type="monotone" dataKey="prosody" stroke="#ec4899" name="韻律" strokeWidth={2} />
                  <Line type="monotone" dataKey="completeness" stroke="#f97316" name="完全性" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-muted-foreground text-center">各採点時のスコアの変化を表示しています</p>
          </TabsContent>

          {/* 平均スコア */}
          <TabsContent value="average" className="space-y-4">
            <div className="h-100 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={averageScoreData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#3b82f6" name="平均スコア" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              全{gradedRecordings.length}件の採点データの平均スコアです
            </p>
          </TabsContent>

          {/* 音素分析 */}
          <TabsContent value="phoneme" className="space-y-4">
            <div className="h-100 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={phonemeStatsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phoneme" />
                  <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#22c55e" domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="count"
                    fill="#3b82f6"
                    stroke="#3b82f6"
                    name="出現回数"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="averageScore"
                    stroke="#22c55e"
                    strokeWidth={2}
                    name="平均スコア"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-muted-foreground text-center">出現回数の多い音素とそのスコアを表示しています</p>
          </TabsContent>

          {/* 苦手な単語 */}
          <TabsContent value="weakWords" className="space-y-4">
            {weakWordsData.length === 0 ? (
              <div className="flex items-center justify-center h-100 text-muted-foreground">
                <p>苦手な単語が見つかりませんでした</p>
              </div>
            ) : (
              <>
                <div className="h-100 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weakWordsData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="word" type="category" width={80} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="averageScore" fill="#ef4444" name="平均スコア" />
                      <Bar dataKey="count" fill="#3b82f6" name="出現回数" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground text-center">
                    苦手と評価された単語の出現回数と平均スコア
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {weakWordsData.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-red-50 dark:bg-red-950/20 p-3 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors"
                        onClick={() => playWordPronunciation(item.word)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm">{item.word}</span>
                          <span className="text-xs text-muted-foreground">×{item.count}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">スコア: {item.averageScore}点</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
